import { useIdentityStore } from '@/store/identity.store';
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { onUnmounted, ref, watch } from 'vue';

// Delay before the terminal-failure `onclose` restart re-attempts. Mirrors the
// tail of SignalR's default automatic-reconnect schedule so a hub that is hard
// down does not get hammered by an immediate tight restart loop.
const ONCLOSE_RESTART_DELAY_MS = 5000;

export function useSignalR(url: string, events: Record<string, (data: any) => void>) {
    const active = ref<boolean>(false);
    const connection = ref<HubConnection | null>(null);
    const isStarting = ref<boolean>(false);
    // True only while `.withAutomaticReconnect()` is actively retrying. While set,
    // the manual `onclose` restart stands down so the two recovery paths never
    // run concurrently.
    const isReconnecting = ref<boolean>(false);
    let oncloseRestartTimer: ReturnType<typeof setTimeout> | null = null;
    const identityStore = useIdentityStore();
    const hasCredentials = () => Boolean(identityStore.data);

    const clearOncloseRestartTimer = () => {
        if (oncloseRestartTimer) {
            clearTimeout(oncloseRestartTimer);
            oncloseRestartTimer = null;
        }
    };

    const buildConnection = () => {
        const conn = new HubConnectionBuilder()
            .withUrl(import.meta.env.VITE_BACKEND_URL + url, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
                withCredentials: true,
            })
            // Primary recovery path: exponential-style backoff for transient
            // drops. While this is retrying, `onclose` does not fire, so there is
            // no overlap with the manual restart below.
            .withAutomaticReconnect()
            .build();

        // Register handlers
        Object.entries(events).forEach(([event, handler]) => {
            conn.on(event, handler);
        });

        conn.onreconnecting(() => {
            isReconnecting.value = true;
        });

        conn.onreconnected(() => {
            isReconnecting.value = false;
        });

        // `onclose` fires either on a deliberate stop (`active` is already false)
        // or when automatic reconnect has exhausted its retries and given up.
        // Only the terminal-failure case needs a manual restart, and it is
        // deferred so it never collides with an in-flight reconnect cycle.
        conn.onclose(() => {
            isReconnecting.value = false;
            if (!active.value || !hasCredentials()) return;

            clearOncloseRestartTimer();
            oncloseRestartTimer = setTimeout(() => {
                oncloseRestartTimer = null;
                void attemptStartConnection();
            }, ONCLOSE_RESTART_DELAY_MS);
        });

        return conn;
    };

    const attemptStartConnection = async () => {
        if (!active.value || !hasCredentials()) return;
        // Defer to automatic reconnect while it owns recovery.
        if (isReconnecting.value) return;
        if (!connection.value) {
            connection.value = buildConnection();
        }
        if (connection.value.state !== 'Disconnected' || isStarting.value) return;

        try {
            isStarting.value = true;
            await connection.value.start();
        } catch (error) {
            console.error('SignalR connection error:', error);
        } finally {
            isStarting.value = false;
        }
    };

    const startConnection = async () => {
        active.value = true;
        await attemptStartConnection();
    };

    const disconnectConnection = async () => {
        clearOncloseRestartTimer();
        isReconnecting.value = false;
        if (connection.value) {
            await connection.value.stop();
            connection.value = null;
        }
    };

    const stopConnection = async () => {
        active.value = false;
        await disconnectConnection();
    };

    watch(
        () => identityStore.data,
        async (data) => {
            if (!active.value) return;
            if (data) {
                await attemptStartConnection();
                return;
            }

            await disconnectConnection();
        }
    );
    onUnmounted(stopConnection);

    return {
        connection,
        startConnection,
        stopConnection,
    };
}
